import {
  type Column,
  type ColumnFiltersState,
  type ColumnDef,
  type FilterFn,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
} from "lucide-react"
import * as React from "react"

import { Button } from "./ui/button"
import { FilterBar, FilterField } from "./filter-bar"
import { ScrollArea, ScrollBar } from "./ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table"
import { cn } from "../lib/utils"

const ALL_FILTER_VALUE = "__erp_all__"
const MAX_FILTER_OPTIONS = 250
const MAX_SELECT_FILTER_OPTIONS = 50
const SELECT_FILTER_KEYWORDS = [
  "active",
  "actif",
  "bank",
  "banque",
  "brand",
  "boutique",
  "canal",
  "category",
  "categorie",
  "channel",
  "drawer",
  "etat",
  "family",
  "famille",
  "frequency",
  "frequence",
  "invoice",
  "marque",
  "method",
  "mode",
  "payment",
  "publication",
  "publie",
  "published",
  "role",
  "source",
  "store",
  "status",
  "statut",
  "type",
  "warehouse",
]

type UseDataTableOptions<TData> = {
  data: Array<TData>
  columns: Array<ColumnDef<TData, unknown>>
  getRowId?: (row: TData) => string
  initialSorting?: SortingState
  pageSize?: number
  columnFilters?: boolean
}

export function useDataTable<TData>(options: UseDataTableOptions<TData>) {
  const { data, columns, getRowId, initialSorting = [], pageSize, columnFilters } = options
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting)
  const [columnFilterState, setColumnFilterState] = React.useState<ColumnFiltersState>([])
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize ?? 10,
  })

  const textFilter = React.useMemo<FilterFn<TData>>(
    () => (row, columnId, filterValue) => {
      const needle = normalizeFilterValue(filterValue).trim()
      if (!needle) return true
      return normalizeFilterValue(row.getValue(columnId)).trim() === needle
    },
    [],
  )

  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: { sorting, pagination, columnFilters: columnFilterState },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnFiltersChange: (updater) => {
      setColumnFilterState(updater)
      setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    },
    defaultColumn: { filterFn: textFilter },
    enableColumnFilters: columnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: columnFilters ? getFilteredRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: pageSize ? getPaginationRowModel() : undefined,
  })

  return { table, sorting, setSorting, pagination, setPagination, columnFilterState, setColumnFilterState }
}

type DataTableColumnHeaderProps<TData, TValue> = {
  column: Column<TData, TValue>
  title: string
  className?: string
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const canSort = column.getCanSort()
  const sorted = column.getIsSorted()

  if (!canSort) {
    return (
      <span
        className={cn(
          "flex h-8 items-center text-xs font-medium text-foreground sm:text-sm",
          className,
        )}
      >
        {title}
      </span>
    )
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        "flex h-8 items-center gap-2 px-0 text-xs font-medium text-foreground sm:text-sm",
        className,
      )}
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      <span>{title}</span>
      {sorted === "desc" ? (
        <ArrowDown className="h-3 w-3 sm:h-4 sm:w-4" />
      ) : sorted === "asc" ? (
        <ArrowUp className="h-3 w-3 sm:h-4 sm:w-4" />
      ) : (
        <ChevronsUpDown className="h-3 w-3 opacity-50 sm:h-4 sm:w-4" />
      )}
    </Button>
  )
}

type DataTableProps<TData> = {
  data: Array<TData>
  columns: Array<ColumnDef<TData, unknown>>
  getRowId?: (row: TData) => string
  initialSorting?: SortingState
  onRowClick?: (row: TData) => void
  emptyMessage?: string
  emptyState?: React.ReactNode
  className?: string
  minWidth?: string | number
  swipeHint?: string
  pagination?: boolean
  pageSize?: number
  pageSizeOptions?: Array<number>
  rowsPerPageLabel?: string
  ofLabel?: string
  isLoading?: boolean
  skeletonRows?: number
  columnFilters?: boolean
}

export function DataTable<TData>({
  data,
  columns,
  getRowId,
  initialSorting,
  onRowClick,
  emptyMessage = "No results.",
  emptyState,
  className,
  minWidth = 800,
  swipeHint,
  pagination,
  pageSize = 10,
  pageSizeOptions = [5, 10, 20, 50],
  rowsPerPageLabel = "Rows per page:",
  ofLabel = "of",
  isLoading,
  skeletonRows = 5,
  columnFilters = true,
}: DataTableProps<TData>) {
  const { table } = useDataTable({
    data,
    columns,
    getRowId,
    initialSorting,
    pageSize: pagination ? pageSize : undefined,
    columnFilters,
  })

  const total = columnFilters ? table.getFilteredRowModel().rows.length : data.length
  const curPage = table.getState().pagination.pageIndex
  const curSize = table.getState().pagination.pageSize
  const startRow = total === 0 ? 0 : curPage * curSize + 1
  const endRow = Math.min((curPage + 1) * curSize, total)
  const pageCount = table.getPageCount()
  const minWidthStyle =
    typeof minWidth === "number" ? `${minWidth}px` : minWidth
  const filterableColumns = columnFilters ? table.getAllLeafColumns().filter((column) => column.getCanFilter()) : []
  const filterRows = table.getPreFilteredRowModel().flatRows
  const columnFilterControls = filterableColumns
    .map((column) => ({
      column,
      label: getColumnFilterLabel(column),
      options: getColumnFilterOptions(filterRows, column),
    }))
    .filter((control) => isSelectFilterColumn(control.column, control.label, control.options))
  const showColumnFilters = columnFilterControls.length > 0
  const hasColumnFilters = table.getState().columnFilters.length > 0

  return (
    <div className={cn("w-full", className)}>
      <div className="w-full overflow-hidden">
        {showColumnFilters && (
          <FilterBar
            className="mb-2"
            onClear={hasColumnFilters ? () => table.resetColumnFilters() : undefined}
            clearLabel="Effacer"
          >
            {columnFilterControls.map(({ column, label, options }) => (
              <FilterField key={`${column.id}-filter`} label={label} minWidth={160}>
                <Select
                  value={normalizeFilterValue(column.getFilterValue()).trim() || ALL_FILTER_VALUE}
                  onValueChange={(value) => column.setFilterValue(value === ALL_FILTER_VALUE ? undefined : value)}
                >
                  <SelectTrigger className="h-8 w-full text-xs">
                    <span className="text-muted-foreground shrink-0">{label}:</span>
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent align="start">
                    <SelectItem value={ALL_FILTER_VALUE}>Tous</SelectItem>
                    {options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
            ))}
          </FilterBar>
        )}
        <ScrollArea className="w-full whitespace-nowrap">
          <div style={{ minWidth: minWidthStyle }}>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="hover:bg-transparent border-b border-border"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        className="h-11 bg-muted/50 px-3 text-start text-xs font-medium select-none first:ps-4 last:pe-4 sm:px-4 sm:text-sm sm:first:ps-5 sm:last:pe-5"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: skeletonRows }).map((_, i) => (
                    <TableRow key={`sk-${i}`} className="border-b border-border/60 last:border-b-0">
                      {columns.map((_, j) => (
                        <TableCell
                          key={j}
                          className="px-3 py-3 text-xs first:ps-4 last:pe-4 sm:px-4 sm:py-3 sm:text-sm sm:first:ps-5 sm:last:pe-5"
                        >
                          <div className="h-4 w-full max-w-[160px] animate-pulse rounded-md bg-muted/70" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      className={cn(
                        "border-b border-border/60 last:border-b-0 hover:bg-muted/50 transition-colors",
                        onRowClick && "cursor-pointer",
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="px-3 py-3 text-xs first:ps-4 last:pe-4 sm:px-4 sm:py-3 sm:text-sm sm:first:ps-5 sm:last:pe-5"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow className="border-0 hover:bg-transparent">
                    <TableCell
                      colSpan={columns.length}
                      className="px-4 text-center text-sm text-muted-foreground"
                    >
                      {emptyState ?? (
                        <div className="py-10">{emptyMessage}</div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
      {swipeHint && (
        <p className="mt-2 text-center text-xs text-muted-foreground sm:hidden">
          {swipeHint}
        </p>
      )}
      {pagination && total > 0 && (
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-1">
          <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
            <span>{rowsPerPageLabel}</span>
            <Select
              value={String(curSize)}
              onValueChange={(v) => table.setPageSize(Number(v))}
            >
              <SelectTrigger className="h-8 w-[68px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((opt) => (
                  <SelectItem key={opt} value={String(opt)} className="text-xs">
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="tabular-nums">
              {startRow}-{endRow} {ofLabel} {total}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              aria-label="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {Array.from({ length: Math.min(pageCount, 5) }, (_, i) => {
              let idx = i
              if (pageCount > 5) {
                if (curPage >= 3 && curPage < pageCount - 2) idx = curPage - 2 + i
                else if (curPage >= pageCount - 2) idx = pageCount - 5 + i
              }
              if (idx >= pageCount) return null
              const active = idx === curPage
              return (
                <Button
                  key={idx}
                  variant={active ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8 text-xs"
                  onClick={() => table.setPageIndex(idx)}
                  aria-label={`Page ${idx + 1}`}
                  aria-current={active ? "page" : undefined}
                >
                  {idx + 1}
                </Button>
              )
            })}
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.setPageIndex(pageCount - 1)}
              disabled={!table.getCanNextPage()}
              aria-label="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function getColumnFilterLabel<TData>(column: Column<TData, unknown>): string {
  const header = column.columnDef.header
  if (typeof header === "string" && header.trim()) return header
  const id = column.id
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
  return id ? id.charAt(0).toUpperCase() + id.slice(1) : "Filtre"
}

function getColumnFilterOptions<TData>(rows: Array<{ getValue: (columnId: string) => unknown }>, column: Column<TData, unknown>) {
  const options = new Map<string, string>()

  for (const row of rows) {
    const rawValue = row.getValue(column.id)
    const value = normalizeFilterValue(rawValue).trim()
    const label = formatFilterOptionLabel(rawValue).trim()
    if (!value || !label) continue
    if (!options.has(value)) options.set(value, label)
    if (options.size >= MAX_FILTER_OPTIONS) break
  }

  return Array.from(options, ([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }))
}

function isSelectFilterColumn<TData>(
  column: Column<TData, unknown>,
  label: string,
  options: Array<{ value: string; label: string }>,
): boolean {
  if (options.length < 2 || options.length > MAX_SELECT_FILTER_OPTIONS) return false
  const filterName = normalizeFilterValue(`${column.id} ${label}`)
  return SELECT_FILTER_KEYWORDS.some((keyword) => filterName.includes(keyword))
}

function formatFilterOptionLabel(value: unknown): string {
  if (value == null) return ""
  if (Array.isArray(value)) return value.map(formatFilterOptionLabel).filter(Boolean).join(" ")
  if (value instanceof Date) return value.toLocaleDateString()
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).map(formatFilterOptionLabel).filter(Boolean).join(" ")
  }
  return String(value)
}

function normalizeFilterValue(value: unknown): string {
  if (value == null) return ""
  if (Array.isArray(value)) return value.map(normalizeFilterValue).join(" ")
  if (value instanceof Date) return value.toISOString().toLowerCase()
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).map(normalizeFilterValue).join(" ")
  }
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
}

export type { ColumnDef, SortingState, Column } from "@tanstack/react-table"
